import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Paths } from 'shared/routes/paths';
import { getPathByString } from 'shared/utils/get-path-by-string';

import { HistoryContext, HistoryContextProps } from './context';
import { RouteHistory } from './types';

type HistoryProviderProps = {
  children: React.ReactNode;
};

export const HistoryProvider: React.FC<HistoryProviderProps> = ({
  children,
}) => {
  const [routeStack, setRouteStack] = useState<RouteHistory[]>([]);

  const { pathname, search } = useLocation();

  useEffect(() => {
    // strict mode has influence on this; first render of the app will add the path twice
    setRouteStack((oldStack) => {
      const path = getPathByString(pathname);
      // filter out a couple of paths that are not relevant for the history
      if (
        [
          undefined,
          Paths.Login,
          Paths.Signup,
          Paths.ForgotPassword,
          Paths.ResetPassword,
          Paths.CompleteSignup,
          Paths.VerifyEmail,
        ].includes(path)
      ) {
        return oldStack;
      }

      const lastRoute = oldStack[oldStack.length - 1];
      if (lastRoute?.pathname === pathname) {
        lastRoute.search = search;
        return [...oldStack];
      }

      return [...oldStack, { pathname, search, path: path! }];
    });
  }, [pathname, search]);

  const pathStack = useMemo(
    () => routeStack.map(({ path }) => path).filter(Boolean) as Paths[],
    [routeStack],
  );

  const value = useMemo<HistoryContextProps>(
    () => ({ pathStack, routeStack }),
    [pathStack, routeStack],
  );

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
};
